/**
 * Capira Header — assets/capira-header.js
 * Scroll-blur, mobile drawer with focus trap, dropdown ARIA, scroll lock.
 * No frameworks. Atelier core untouched.
 */

(function () {
  'use strict';

  const pref = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const header    = document.getElementById('capira-header');
  const menuBtn   = document.getElementById('capira-menu-btn');
  const closeBtn  = document.getElementById('capira-mobile-close');
  const mobileNav = document.getElementById('capira-mobile-nav');
  const overlay   = document.getElementById('capira-mobile-overlay');

  if (!header) return;

  const threshold = parseInt(header.dataset.scrollThreshold || '16', 10);

  /* ════════════════════════════════════════════
     1. SCROLL
  ════════════════════════════════════════════ */
  let ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      header.classList.toggle('is-scrolled', window.scrollY > threshold);
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ════════════════════════════════════════════
     2. DARK SURFACE AUTO-DETECT
  ════════════════════════════════════════════ */
  (function detectDarkSurface() {
    const candidates = [
      '.capira-hero-section',
      '[data-capira-dark-hero]',
      '.capira-header-section + .shopify-section [class*="dark"]',
    ];
    const darkEl = document.querySelector(candidates.join(','));
    if (darkEl) {
      const rect = darkEl.getBoundingClientRect();
      if (rect.top <= 80) header.classList.add('is-dark-surface');
    }
  })();

  /* ════════════════════════════════════════════
     3. SCROLLBAR WIDTH
  ════════════════════════════════════════════ */
  (function cacheScrollbarWidth() {
    const w = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--capira-scrollbar-width', `${w}px`);
  })();

  /* ════════════════════════════════════════════
     4. DESKTOP DROPDOWN
  ════════════════════════════════════════════ */
  header.querySelectorAll('.capira-header__nav-trigger').forEach(trigger => {
    const ddId = trigger.getAttribute('aria-controls');
    const dd   = ddId && document.getElementById(ddId);
    if (!dd) return;

    const item = trigger.closest('.capira-header__nav-item');

    function open()  { trigger.setAttribute('aria-expanded', 'true');  dd.classList.add('is-open'); }
    function close() { trigger.setAttribute('aria-expanded', 'false'); dd.classList.remove('is-open'); }

    trigger.addEventListener('click', () => trigger.getAttribute('aria-expanded') === 'true' ? close() : open());

    trigger.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); trigger.getAttribute('aria-expanded') === 'true' ? close() : open(); }
      if (e.key === 'Escape') { close(); trigger.focus(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); open(); const f = dd.querySelector('a'); if (f) f.focus(); }
    });

    dd.addEventListener('keydown', e => { if (e.key === 'Escape') { close(); trigger.focus(); } });
    document.addEventListener('click', e => { if (item && !item.contains(e.target)) close(); });
  });

  /* ════════════════════════════════════════════
     5. MOBILE DRAWER
  ════════════════════════════════════════════ */
  if (!menuBtn || !mobileNav || !overlay || !closeBtn) return;

  let drawerOpen = false;

  function focusable() {
    return Array.from(
      mobileNav.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
  }

  function trapFocus(e) {
    const els = focusable();
    const first = els[0];
    const last  = els[els.length - 1];
    if (!first) return;
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    if (e.key === 'Escape') closeDrawer();
  }

  function staggerReveal() {
    const items = mobileNav.querySelectorAll('.capira-mobile-nav__item');
    if (pref) { items.forEach(el => el.classList.add('is-revealed')); return; }
    items.forEach((el, i) => setTimeout(() => el.classList.add('is-revealed'), 50 + i * 55));
  }

  function staggerHide() {
    mobileNav.querySelectorAll('.capira-mobile-nav__item').forEach(el => el.classList.remove('is-revealed'));
  }

  function openDrawer() {
    drawerOpen = true;
    document.body.classList.add('capira-nav-open');
    mobileNav.classList.add('is-open');
    mobileNav.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    menuBtn.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', trapFocus);
    staggerReveal();
    setTimeout(() => { const f = focusable()[0]; if (f) f.focus(); }, pref ? 0 : 400);
  }

  function closeDrawer() {
    drawerOpen = false;
    document.body.classList.remove('capira-nav-open');
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    menuBtn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', trapFocus);
    menuBtn.focus();
    setTimeout(staggerHide, pref ? 0 : 320);
  }

  menuBtn.addEventListener('click', () => drawerOpen ? closeDrawer() : openDrawer());
  closeBtn.addEventListener('click', closeDrawer);
  overlay.addEventListener('click', closeDrawer);

  /* ════════════════════════════════════════════
     6. MOBILE SUB-MENU ACCORDION
  ════════════════════════════════════════════ */
  mobileNav.querySelectorAll('.capira-mobile-nav__group-btn').forEach(btn => {
    const sub = btn.nextElementSibling;
    if (!sub) return;
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      sub.classList.toggle('is-open', !open);
      sub.setAttribute('aria-hidden', String(open));
    });
  });

  /* ════════════════════════════════════════════
     7. SHOPIFY THEME EDITOR COMPAT
  ════════════════════════════════════════════ */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', e => {
      if (e.target.contains(header)) onScroll();
    });
  }

})();
